import {
  AnalyticsUpIcon,
  Award01Icon,
  BarChartIcon,
  ChampionIcon,
  InformationCircleIcon,
  Medal01Icon,
  StarAward01Icon,
  TaskDone01Icon,
  Timer01Icon,
  User02Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { createFileRoute } from '@tanstack/react-router';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { apiGet } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { useGenerateReport } from '@/hooks/use-reports';

export const Route = createFileRoute('/_authenticated/analytics/personnel-performance')({
  component: AnalyticsPersonnelPerformance,
});

// Types for our analytics
interface PerformanceMetric {
  userId: string;
  userName: string;
  groupName: string;
  totalTickets: number;
  avgTimeToRepair: number;
  avgResponseTime: number;
  firstTimeFixRate: number;
  efficiencyScore: number;
  // NEW: Recurrence metrics
  recurrenceRate: number;
  recurredTickets: number;
}

interface PerformanceInsight {
  category: 'training' | 'evaluation' | 'reward';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  icon: string;
  users: Array<{ name: string; metric: string; value: string }>;
}

interface PerformanceData {
  metrics: PerformanceMetric[];
  insights: PerformanceInsight[];
  summary: {
    totalTickets: number;
    totalTechnicians: number;
    avgEfficiency: number;
  };
}

// Types for PM analytics
interface PMPerformanceMetric {
  userId: string;
  userName: string;
  groupName: string;
  totalTickets: number;
  avgResponseTime: number;
  avgActualDuration: number;
  avgEstimatedDuration: number;
  durationAccuracy: number;
  efficiencyScore: number;
}

interface PMPerformanceData {
  metrics: PMPerformanceMetric[];
  insights: PerformanceInsight[];
  summary: {
    totalTickets: number;
    totalTechnicians: number;
    avgEfficiency: number;
  };
}

// Utility functions
function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// Helper component for table head with tooltip
function TableHeadWithTooltip({
  children,
  tooltipKey,
  t,
}: {
  children: ReactNode;
  tooltipKey: string;
  t: (key: string) => string;
}) {
  return (
    <TableHead className="h-8 text-xs text-center">
      <div className="flex items-center justify-center gap-1">
        {children}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="hover:text-foreground/80">
              <HugeiconsIcon
                icon={InformationCircleIcon}
                size={12}
                className="text-muted-foreground"
              />
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[220px] whitespace-pre-line">
              <p className="text-xs">
                {t(`analytics.personnelPerformance.messages.tooltip.${tooltipKey}`)}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </TableHead>
  );
}

function formatDuration(hours: number): string {
  if (hours < 1) {
    return `${Math.round(hours * 60)}m`;
  }
  return `${hours.toFixed(1)}h`;
}

function getIconForInsight(iconName: string): any {
  switch (iconName) {
    case 'Timer01Icon':
      return Timer01Icon;
    case 'AnalyticsUpIcon':
      return AnalyticsUpIcon;
    case 'User02Icon':
      return User02Icon;
    case 'Award01Icon':
      return Award01Icon;
    default:
      return StarAward01Icon;
  }
}

function getCategoryColor(category: string): {
  bg: string;
  border: string;
  text: string;
  iconBg: string;
} {
  switch (category) {
    case 'training':
      return {
        bg: 'bg-rose-50 dark:bg-rose-950/30',
        border: 'border-rose-200/50 dark:border-rose-800/50',
        text: 'text-rose-600 dark:text-rose-400',
        iconBg: 'bg-rose-500/10',
      };
    case 'evaluation':
      return {
        bg: 'bg-amber-50 dark:bg-amber-950/30',
        border: 'border-amber-200/50 dark:border-amber-800/50',
        text: 'text-amber-600 dark:text-amber-400',
        iconBg: 'bg-amber-500/10',
      };
    case 'reward':
      return {
        bg: 'bg-emerald-50 dark:bg-emerald-950/30',
        border: 'border-emerald-200/50 dark:border-emerald-800/50',
        text: 'text-emerald-600 dark:text-emerald-400',
        iconBg: 'bg-emerald-500/10',
      };
    default:
      return {
        bg: 'bg-muted/30',
        border: 'border-border/50',
        text: 'text-muted-foreground',
        iconBg: 'bg-muted/50',
      };
  }
}

function AnalyticsPersonnelPerformance() {
  const { t } = useTranslation();
  const [data, setData] = useState<PerformanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'corrective' | 'preventive'>('corrective');
  const [pmData, setPmData] = useState<PMPerformanceData | null>(null);
  const [pmLoading, setPmLoading] = useState(true);
  const [pmError, setPmError] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const generateReport = useGenerateReport();

  // Fetch analytics data
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);
        const response = await apiGet<PerformanceData>('/analytics/personnel-performance?days=30');
        setData(response);
      } catch (err) {
        console.error('Failed to fetch performance data:', err);
        setError(t('analytics.personnelPerformance.messages.failedToLoad'));
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [t]);

  // Fetch PM analytics data
  useEffect(() => {
    async function fetchPMData() {
      try {
        setPmLoading(true);
        setPmError(null);
        const response = await apiGet<PMPerformanceData>(
          '/analytics/pm-personnel-performance?days=30',
        );
        setPmData(response);
      } catch (err) {
        console.error('Failed to fetch PM performance data:', err);
        setPmError(t('analytics.personnelPerformance.messages.failedToLoad'));
      } finally {
        setPmLoading(false);
      }
    }
    fetchPMData();
  }, [t]);

  // Group insights by category
  const trainingInsights = data?.insights.filter((i) => i.category === 'training') ?? [];
  const evaluationInsights = data?.insights.filter((i) => i.category === 'evaluation') ?? [];
  const rewardInsights = data?.insights.filter((i) => i.category === 'reward') ?? [];

  // Top performers
  const topPerformers = [...(data?.metrics ?? [])]
    .sort((a, b) => b.efficiencyScore - a.efficiencyScore)
    .slice(0, 5);

  const quickStats = [
    {
      label: t('analytics.personnelPerformance.stats.technicians'),
      value: data?.summary.totalTechnicians ?? 0,
      icon: User02Icon,
      color: 'text-primary',
      bg: 'bg-primary/10',
    },
    {
      label: t('analytics.personnelPerformance.stats.totalTickets'),
      value: data?.summary.totalTickets ?? 0,
      icon: TaskDone01Icon,
      color: 'text-blue-600 dark:text-blue-400',
      bg: 'bg-blue-500/10',
    },
    {
      label: t('analytics.personnelPerformance.stats.avgEfficiency'),
      value: `${data?.summary.avgEfficiency ?? 0}%`,
      icon: AnalyticsUpIcon,
      color: 'text-emerald-600 dark:text-emerald-400',
      bg: 'bg-emerald-500/10',
    },
    {
      label: t('analytics.personnelPerformance.stats.topPerformers'),
      value: rewardInsights.length,
      icon: Award01Icon,
      color: 'text-amber-600 dark:text-amber-400',
      bg: 'bg-amber-500/10',
    },
  ];

  return (
    <div className="flex flex-col gap-3 sm:gap-4">
      {/* Header */}
      <div className="hidden md:flex md:items-center md:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">
            {t('analytics.personnelPerformance.title')}
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {t('analytics.personnelPerformance.description')}
          </p>
        </div>
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExportOpen(!exportOpen)}
            disabled={generateReport.isPending}
            className="h-8"
          >
            {generateReport.isPending ? 'Generating...' : 'Export'}
          </Button>
          {exportOpen && (
            <div className="absolute right-0 top-full mt-1 z-10 bg-background border rounded-md shadow-md py-1 w-24">
              {(['pdf', 'csv', 'xlsx'] as const).map((fmt) => (
                <button
                  key={fmt}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent uppercase"
                  onClick={() => {
                    setExportOpen(false);
                    const dateTo = new Date().toISOString().split('T')[0];
                    const dateFrom = new Date(Date.now() - 30 * 86400000)
                      .toISOString()
                      .split('T')[0];
                    generateReport.mutate({
                      template: 'team-performance',
                      format: fmt,
                      dateFrom,
                      dateTo,
                    });
                  }}
                >
                  {fmt}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tabs for Corrective/Preventive */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as 'corrective' | 'preventive')}
        className="w-full"
      >
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="corrective">
            {t('analytics.personnelPerformance.tabs.corrective')}
          </TabsTrigger>
          <TabsTrigger value="preventive">
            {t('analytics.personnelPerformance.tabs.preventive')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="corrective" className="mt-4 space-y-3 sm:space-y-4">
          {loading ? (
            <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
              {quickStats.map((_, i) => (
                <Card key={i} className="py-3 gap-2">
                  <CardContent className="p-4">
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-8 w-16" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : error ? (
            <Card className="border-destructive/50">
              <CardContent className="p-6 text-center">
                <HugeiconsIcon
                  icon={AnalyticsUpIcon}
                  className="h-10 w-10 mx-auto mb-2 text-muted-foreground"
                />
                <p className="text-sm text-muted-foreground">{error}</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Quick Stats */}
              <div className="grid gap-2 sm:gap-3 grid-cols-2 md:grid-cols-2 lg:grid-cols-4">
                {quickStats.map((stat) => (
                  <Card key={stat.label} className="py-3 gap-2 metric-card">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                      <CardTitle className="text-xs font-medium pb-0 uppercase tracking-wide text-muted-foreground">
                        {stat.label}
                      </CardTitle>
                      <div className={`${stat.bg} p-2 rounded-md`}>
                        <HugeiconsIcon icon={stat.icon} size={16} className={stat.color} />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="metric-value">{stat.value}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Insights Grid */}
              <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {/* Training Needs */}
                <Card className="gap-2 border-rose-200/50 dark:border-rose-900/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-sm">
                      <div className="h-8 w-8 rounded-lg bg-rose-500/10 flex items-center justify-center">
                        <HugeiconsIcon
                          icon={Timer01Icon}
                          size={16}
                          className="text-rose-600 dark:text-rose-400"
                        />
                      </div>
                      {t('analytics.personnelPerformance.cards.needsTraining')}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {t('analytics.personnelPerformance.descriptions.slowFixTimes')} →{' '}
                      {trainingInsights.reduce((sum, i) => sum + i.users.length, 0)}{' '}
                      {t('analytics.personnelPerformance.stats.technicians').toLowerCase()}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {trainingInsights.length === 0 ? (
                      <div className="py-6 text-center">
                        <HugeiconsIcon
                          icon={StarAward01Icon}
                          size={24}
                          className="mx-auto mb-2 text-emerald-500"
                        />
                        <p className="text-xs text-muted-foreground">
                          {t('analytics.personnelPerformance.messages.allPerformingWell')}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {trainingInsights.map((insight, idx) => {
                          const colors = getCategoryColor(insight.category);
                          const Icon = getIconForInsight(insight.icon);
                          return (
                            <div
                              key={idx}
                              className="p-3 rounded-lg bg-muted/30 border border-border/50"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <div
                                  className={cn(
                                    'h-7 w-7 rounded-md flex items-center justify-center',
                                    colors.iconBg,
                                  )}
                                >
                                  <HugeiconsIcon icon={Icon} size={14} className={colors.text} />
                                </div>
                                <span className="text-sm font-medium">{insight.title}</span>
                              </div>
                              <p className="text-xs text-muted-foreground mb-2">
                                {insight.description}
                              </p>
                              <div className="space-y-1">
                                {insight.users.map((user, uIdx) => (
                                  <div
                                    key={uIdx}
                                    className="flex items-center justify-between text-xs"
                                  >
                                    <span className="text-muted-foreground">{user.name}</span>
                                    <span className="font-medium">{user.value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Evaluation Needs */}
                <Card className="gap-2 border-amber-200/50 dark:border-amber-900/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-sm">
                      <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                        <HugeiconsIcon
                          icon={AnalyticsUpIcon}
                          size={16}
                          className="text-amber-600 dark:text-amber-400"
                        />
                      </div>
                      {t('analytics.personnelPerformance.cards.needsEvaluation')}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {t('analytics.personnelPerformance.descriptions.responseWorkloadIssues')} →{' '}
                      {evaluationInsights.reduce((sum, i) => sum + i.users.length, 0)}{' '}
                      {t('analytics.personnelPerformance.stats.technicians').toLowerCase()}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {evaluationInsights.length === 0 ? (
                      <div className="py-6 text-center">
                        <HugeiconsIcon
                          icon={StarAward01Icon}
                          size={24}
                          className="mx-auto mb-2 text-emerald-500"
                        />
                        <p className="text-xs text-muted-foreground">
                          {t('analytics.personnelPerformance.messages.noEvaluationConcerns')}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {evaluationInsights.map((insight, idx) => {
                          const colors = getCategoryColor(insight.category);
                          const Icon = getIconForInsight(insight.icon);
                          return (
                            <div
                              key={idx}
                              className="p-3 rounded-lg bg-muted/30 border border-border/50"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <div
                                  className={cn(
                                    'h-7 w-7 rounded-md flex items-center justify-center',
                                    colors.iconBg,
                                  )}
                                >
                                  <HugeiconsIcon icon={Icon} size={14} className={colors.text} />
                                </div>
                                <span className="text-sm font-medium">{insight.title}</span>
                              </div>
                              <p className="text-xs text-muted-foreground mb-2">
                                {insight.description}
                              </p>
                              <div className="space-y-1">
                                {insight.users.map((user, uIdx) => (
                                  <div
                                    key={uIdx}
                                    className="flex items-center justify-between text-xs"
                                  >
                                    <span className="text-muted-foreground">{user.name}</span>
                                    <span className="font-medium">{user.value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Top Performers */}
                <Card className="gap-2 border-emerald-200/50 dark:border-emerald-900/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-sm">
                      <div className="h-8 w-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                        <HugeiconsIcon
                          icon={Award01Icon}
                          size={16}
                          className="text-emerald-600 dark:text-emerald-400"
                        />
                      </div>
                      {t('analytics.personnelPerformance.cards.topPerformers')}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {t('analytics.personnelPerformance.descriptions.excellenceMetrics')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {topPerformers.length === 0 ? (
                      <div className="py-6 text-center">
                        <p className="text-xs text-muted-foreground">
                          {t('analytics.personnelPerformance.messages.noDataAvailable')}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {topPerformers.slice(0, 4).map((tech, index) => (
                          <div
                            key={tech.userId}
                            className={cn(
                              'flex items-center gap-3 p-2 rounded-md border transition-all',
                              index === 0
                                ? 'bg-gradient-to-r from-emerald-50 to-amber-50 dark:from-emerald-950/30 dark:to-amber-950/30 border-emerald-200/50 dark:border-emerald-800/50'
                                : 'bg-muted/30 border-border/50 hover:bg-muted/50',
                            )}
                          >
                            <div className="shrink-0 w-6 text-center">
                              {index === 0 ? (
                                <HugeiconsIcon
                                  icon={ChampionIcon}
                                  size={16}
                                  className="text-amber-500 mx-auto"
                                />
                              ) : index === 1 ? (
                                <HugeiconsIcon
                                  icon={Medal01Icon}
                                  size={16}
                                  className="text-muted-foreground mx-auto"
                                />
                              ) : index === 2 ? (
                                <HugeiconsIcon
                                  icon={Award01Icon}
                                  size={16}
                                  className="text-amber-700 mx-auto"
                                />
                              ) : (
                                <span className="text-xs font-bold text-muted-foreground">
                                  #{index + 1}
                                </span>
                              )}
                            </div>
                            <Avatar className="h-7 w-7">
                              <AvatarFallback className="text-[10px] font-semibold bg-primary/20 text-primary">
                                {getInitials(tech.userName)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold truncate">{tech.userName}</p>
                              <p className="text-[10px] text-muted-foreground">{tech.groupName}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                                {tech.efficiencyScore}%
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Full Performance Table */}
              <Card className="gap-2">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <HugeiconsIcon icon={BarChartIcon} size={16} />
                    {t('analytics.personnelPerformance.table.fullBreakdown')}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {t('analytics.personnelPerformance.table.allTechnicians')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md p-2 border bg-sidebar/20 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="h-8 text-xs">
                            {t('analytics.personnelPerformance.table.technician')}
                          </TableHead>
                          <TableHead className="h-8 text-xs text-center">
                            {t('analytics.personnelPerformance.table.group')}
                          </TableHead>
                          <TableHead className="h-8 text-xs text-center">
                            {t('analytics.personnelPerformance.table.tickets')}
                          </TableHead>
                          <TableHeadWithTooltip tooltipKey="avgTTR" t={t}>
                            {t('analytics.personnelPerformance.table.avgTTR')}
                          </TableHeadWithTooltip>
                          <TableHeadWithTooltip tooltipKey="response" t={t}>
                            {t('analytics.personnelPerformance.table.response')}
                          </TableHeadWithTooltip>
                          <TableHeadWithTooltip tooltipKey="ftfRate" t={t}>
                            {t('analytics.personnelPerformance.table.ftfRate')}
                          </TableHeadWithTooltip>
                          <TableHeadWithTooltip tooltipKey="recurrence" t={t}>
                            {t('analytics.personnelPerformance.table.recurrence')}
                          </TableHeadWithTooltip>
                          <TableHeadWithTooltip tooltipKey="efficiencyCorrective" t={t}>
                            {t('analytics.personnelPerformance.table.efficiency')}
                          </TableHeadWithTooltip>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data?.metrics && data.metrics.length > 0 ? (
                          [...data.metrics]
                            .sort((a, b) => b.efficiencyScore - a.efficiencyScore)
                            .map((tech) => (
                              <TableRow key={tech.userId}>
                                <TableCell className="text-xs font-medium">
                                  <div className="flex items-center gap-2">
                                    <Avatar className="h-6 w-6">
                                      <AvatarFallback className="text-[9px] font-semibold bg-primary/20 text-primary">
                                        {getInitials(tech.userName)}
                                      </AvatarFallback>
                                    </Avatar>
                                    {tech.userName}
                                  </div>
                                </TableCell>
                                <TableCell className="text-xs text-center text-muted-foreground">
                                  {tech.groupName}
                                </TableCell>
                                <TableCell className="text-xs text-center">
                                  {tech.totalTickets}
                                </TableCell>
                                <TableCell className="text-xs text-center">
                                  {formatDuration(tech.avgTimeToRepair)}
                                </TableCell>
                                <TableCell className="text-xs text-center">
                                  {Math.round(tech.avgResponseTime)}m
                                </TableCell>
                                <TableCell className="text-xs text-center">
                                  {tech.firstTimeFixRate}%
                                </TableCell>
                                <TableCell className="text-xs text-center">
                                  <span
                                    className={cn(
                                      tech.recurrenceRate <= 5
                                        ? 'text-emerald-600 dark:text-emerald-400'
                                        : tech.recurrenceRate <= 15
                                          ? 'text-yellow-600 dark:text-yellow-400'
                                          : 'text-rose-600 dark:text-rose-400',
                                    )}
                                  >
                                    {tech.recurrenceRate}%
                                  </span>
                                </TableCell>
                                <TableCell className="text-xs text-center">
                                  <div className="flex items-center justify-center gap-1.5">
                                    <div className="h-1.5 w-12 bg-muted rounded-full overflow-hidden">
                                      <div
                                        className={cn(
                                          'h-full rounded-full',
                                          tech.efficiencyScore >= 90
                                            ? 'bg-emerald-500'
                                            : tech.efficiencyScore >= 75
                                              ? 'bg-yellow-500'
                                              : 'bg-rose-500',
                                        )}
                                        style={{ width: `${tech.efficiencyScore}%` }}
                                      />
                                    </div>
                                    <span className="text-xs font-bold">
                                      {tech.efficiencyScore}%
                                    </span>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={8}
                              className="h-24 text-center text-xs text-muted-foreground"
                            >
                              {t('analytics.personnelPerformance.messages.noDataAvailable')}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="preventive" className="mt-4 space-y-3 sm:space-y-4">
          {pmLoading ? (
            <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
              {[0, 1, 2, 3].map((i) => (
                <Card key={i} className="py-3 gap-2">
                  <CardContent className="p-4">
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-8 w-16" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : pmError ? (
            <Card className="border-destructive/50">
              <CardContent className="p-6 text-center">
                <HugeiconsIcon
                  icon={AnalyticsUpIcon}
                  className="h-10 w-10 mx-auto mb-2 text-muted-foreground"
                />
                <p className="text-sm text-muted-foreground">{pmError}</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Quick Stats */}
              <div className="grid gap-2 sm:gap-3 grid-cols-2 md:grid-cols-2 lg:grid-cols-4">
                {[
                  {
                    label: t('analytics.personnelPerformance.stats.technicians'),
                    value: pmData?.summary.totalTechnicians ?? 0,
                    icon: User02Icon,
                    color: 'text-primary',
                    bg: 'bg-primary/10',
                  },
                  {
                    label: t('analytics.personnelPerformance.stats.totalTickets'),
                    value: pmData?.summary.totalTickets ?? 0,
                    icon: TaskDone01Icon,
                    color: 'text-blue-600 dark:text-blue-400',
                    bg: 'bg-blue-500/10',
                  },
                  {
                    label: t('analytics.personnelPerformance.stats.avgEfficiency'),
                    value: `${pmData?.summary.avgEfficiency ?? 0}%`,
                    icon: AnalyticsUpIcon,
                    color: 'text-emerald-600 dark:text-emerald-400',
                    bg: 'bg-emerald-500/10',
                  },
                  {
                    label: t('analytics.personnelPerformance.stats.topPerformers'),
                    value: pmData?.metrics
                      ? Math.min(pmData.metrics.filter((m) => m.efficiencyScore >= 85).length, 4)
                      : 0,
                    icon: Award01Icon,
                    color: 'text-amber-600 dark:text-amber-400',
                    bg: 'bg-amber-500/10',
                  },
                ].map((stat) => (
                  <Card key={stat.label} className="py-3 gap-2 metric-card">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                      <CardTitle className="text-xs font-medium pb-0 uppercase tracking-wide text-muted-foreground">
                        {stat.label}
                      </CardTitle>
                      <div className={`${stat.bg} p-2 rounded-md`}>
                        <HugeiconsIcon icon={stat.icon} size={16} className={stat.color} />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="metric-value">{stat.value}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Insights Grid */}
              <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {/* Training Needs */}
                <Card className="gap-2 border-rose-200/50 dark:border-rose-900/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-sm">
                      <div className="h-8 w-8 rounded-lg bg-rose-500/10 flex items-center justify-center">
                        <HugeiconsIcon
                          icon={Timer01Icon}
                          size={16}
                          className="text-rose-600 dark:text-rose-400"
                        />
                      </div>
                      {t('analytics.personnelPerformance.cards.needsTraining')}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {t('analytics.personnelPerformance.descriptions.pmResponseWorkloadIssues')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {(pmData?.insights.filter((i) => i.category === 'training') ?? []).length ===
                    0 ? (
                      <div className="py-6 text-center">
                        <HugeiconsIcon
                          icon={StarAward01Icon}
                          size={24}
                          className="mx-auto mb-2 text-emerald-500"
                        />
                        <p className="text-xs text-muted-foreground">
                          {t('analytics.personnelPerformance.messages.allPerformingWell')}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {(pmData?.insights.filter((i) => i.category === 'training') ?? []).map(
                          (insight, idx) => {
                            const colors = getCategoryColor(insight.category);
                            const Icon = getIconForInsight(insight.icon);
                            return (
                              <div
                                key={idx}
                                className="p-3 rounded-lg bg-muted/30 border border-border/50"
                              >
                                <div className="flex items-center gap-2 mb-2">
                                  <div
                                    className={cn(
                                      'h-7 w-7 rounded-md flex items-center justify-center',
                                      colors.iconBg,
                                    )}
                                  >
                                    <HugeiconsIcon icon={Icon} size={14} className={colors.text} />
                                  </div>
                                  <span className="text-sm font-medium">{insight.title}</span>
                                </div>
                                <p className="text-xs text-muted-foreground mb-2">
                                  {insight.description}
                                </p>
                                <div className="space-y-1">
                                  {insight.users.map((user, uIdx) => (
                                    <div
                                      key={uIdx}
                                      className="flex items-center justify-between text-xs"
                                    >
                                      <span className="text-muted-foreground">{user.name}</span>
                                      <span className="font-medium">{user.value}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          },
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Evaluation Needs */}
                <Card className="gap-2 border-amber-200/50 dark:border-amber-900/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-sm">
                      <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                        <HugeiconsIcon
                          icon={AnalyticsUpIcon}
                          size={16}
                          className="text-amber-600 dark:text-amber-400"
                        />
                      </div>
                      {t('analytics.personnelPerformance.cards.needsEvaluation')}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {t('analytics.personnelPerformance.descriptions.pmResponseWorkloadIssues')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {(pmData?.insights.filter((i) => i.category === 'evaluation') ?? []).length ===
                    0 ? (
                      <div className="py-6 text-center">
                        <HugeiconsIcon
                          icon={StarAward01Icon}
                          size={24}
                          className="mx-auto mb-2 text-emerald-500"
                        />
                        <p className="text-xs text-muted-foreground">
                          {t('analytics.personnelPerformance.messages.noEvaluationConcerns')}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {(pmData?.insights.filter((i) => i.category === 'evaluation') ?? []).map(
                          (insight, idx) => {
                            const colors = getCategoryColor(insight.category);
                            const Icon = getIconForInsight(insight.icon);
                            return (
                              <div
                                key={idx}
                                className="p-3 rounded-lg bg-muted/30 border border-border/50"
                              >
                                <div className="flex items-center gap-2 mb-2">
                                  <div
                                    className={cn(
                                      'h-7 w-7 rounded-md flex items-center justify-center',
                                      colors.iconBg,
                                    )}
                                  >
                                    <HugeiconsIcon icon={Icon} size={14} className={colors.text} />
                                  </div>
                                  <span className="text-sm font-medium">{insight.title}</span>
                                </div>
                                <p className="text-xs text-muted-foreground mb-2">
                                  {insight.description}
                                </p>
                                <div className="space-y-1">
                                  {insight.users.map((user, uIdx) => (
                                    <div
                                      key={uIdx}
                                      className="flex items-center justify-between text-xs"
                                    >
                                      <span className="text-muted-foreground">{user.name}</span>
                                      <span className="font-medium">{user.value}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          },
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Top Performers */}
                <Card className="gap-2 border-emerald-200/50 dark:border-emerald-900/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-sm">
                      <div className="h-8 w-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                        <HugeiconsIcon
                          icon={Award01Icon}
                          size={16}
                          className="text-emerald-600 dark:text-emerald-400"
                        />
                      </div>
                      {t('analytics.personnelPerformance.cards.topPerformers')}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {t('analytics.personnelPerformance.descriptions.excellenceMetrics')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {(pmData?.metrics ?? []).length === 0 ? (
                      <div className="py-6 text-center">
                        <p className="text-xs text-muted-foreground">
                          {t('analytics.personnelPerformance.messages.noDataAvailable')}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {[...(pmData?.metrics ?? [])]
                          .sort((a, b) => b.efficiencyScore - a.efficiencyScore)
                          .slice(0, 4)
                          .map((tech, index) => (
                            <div
                              key={tech.userId}
                              className={cn(
                                'flex items-center gap-3 p-2 rounded-md border transition-all',
                                index === 0
                                  ? 'bg-gradient-to-r from-emerald-50 to-amber-50 dark:from-emerald-950/30 dark:to-amber-950/30 border-emerald-200/50 dark:border-emerald-800/50'
                                  : 'bg-muted/30 border-border/50 hover:bg-muted/50',
                              )}
                            >
                              <div className="shrink-0 w-6 text-center">
                                {index === 0 ? (
                                  <HugeiconsIcon
                                    icon={ChampionIcon}
                                    size={16}
                                    className="text-amber-500 mx-auto"
                                  />
                                ) : index === 1 ? (
                                  <HugeiconsIcon
                                    icon={Medal01Icon}
                                    size={16}
                                    className="text-muted-foreground mx-auto"
                                  />
                                ) : index === 2 ? (
                                  <HugeiconsIcon
                                    icon={Award01Icon}
                                    size={16}
                                    className="text-amber-700 mx-auto"
                                  />
                                ) : (
                                  <span className="text-xs font-bold text-muted-foreground">
                                    #{index + 1}
                                  </span>
                                )}
                              </div>
                              <Avatar className="h-7 w-7">
                                <AvatarFallback className="text-[10px] font-semibold bg-primary/20 text-primary">
                                  {getInitials(tech.userName)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold truncate">{tech.userName}</p>
                                <p className="text-[10px] text-muted-foreground">
                                  {tech.groupName}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                                  {tech.efficiencyScore}%
                                </p>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* PM Performance Table */}
              <Card className="gap-2">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <HugeiconsIcon icon={BarChartIcon} size={16} />
                    {t('analytics.personnelPerformance.table.fullBreakdown')}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {t('analytics.personnelPerformance.table.allTechnicians')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md p-2 border bg-sidebar/20 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="h-8 text-xs">
                            {t('analytics.personnelPerformance.table.technician')}
                          </TableHead>
                          <TableHead className="h-8 text-xs text-center">
                            {t('analytics.personnelPerformance.table.group')}
                          </TableHead>
                          <TableHead className="h-8 text-xs text-center">
                            {t('analytics.personnelPerformance.table.tickets')}
                          </TableHead>
                          <TableHeadWithTooltip tooltipKey="response" t={t}>
                            {t('analytics.personnelPerformance.table.response')}
                          </TableHeadWithTooltip>
                          <TableHead className="h-8 text-xs text-center">
                            {t('analytics.personnelPerformance.table.avgActualDuration')}
                          </TableHead>
                          <TableHead className="h-8 text-xs text-center">
                            {t('analytics.personnelPerformance.table.avgEstimatedDuration')}
                          </TableHead>
                          <TableHead className="h-8 text-xs text-center">
                            {t('analytics.personnelPerformance.table.durationAccuracy')}
                          </TableHead>
                          <TableHeadWithTooltip tooltipKey="efficiencyPreventive" t={t}>
                            {t('analytics.personnelPerformance.table.efficiency')}
                          </TableHeadWithTooltip>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pmData?.metrics && pmData.metrics.length > 0 ? (
                          [...pmData.metrics]
                            .sort((a, b) => b.efficiencyScore - a.efficiencyScore)
                            .map((tech) => (
                              <TableRow key={tech.userId}>
                                <TableCell className="text-xs font-medium">
                                  <div className="flex items-center gap-2">
                                    <Avatar className="h-6 w-6">
                                      <AvatarFallback className="text-[9px] font-semibold bg-primary/20 text-primary">
                                        {getInitials(tech.userName)}
                                      </AvatarFallback>
                                    </Avatar>
                                    {tech.userName}
                                  </div>
                                </TableCell>
                                <TableCell className="text-xs text-center text-muted-foreground">
                                  {tech.groupName}
                                </TableCell>
                                <TableCell className="text-xs text-center">
                                  {tech.totalTickets}
                                </TableCell>
                                <TableCell className="text-xs text-center">
                                  {Math.round(tech.avgResponseTime)}m
                                </TableCell>
                                <TableCell className="text-xs text-center">
                                  {formatDuration(tech.avgActualDuration / 60)}
                                </TableCell>
                                <TableCell className="text-xs text-center">
                                  {formatDuration(tech.avgEstimatedDuration / 60)}
                                </TableCell>
                                <TableCell className="text-xs text-center">
                                  <span
                                    className={cn(
                                      tech.durationAccuracy >= 85
                                        ? 'text-emerald-600 dark:text-emerald-400'
                                        : tech.durationAccuracy >= 70
                                          ? 'text-yellow-600 dark:text-yellow-400'
                                          : 'text-rose-600 dark:text-rose-400',
                                    )}
                                  >
                                    {tech.durationAccuracy}%
                                  </span>
                                </TableCell>
                                <TableCell className="text-xs text-center">
                                  <div className="flex items-center justify-center gap-1.5">
                                    <div className="h-1.5 w-12 bg-muted rounded-full overflow-hidden">
                                      <div
                                        className={cn(
                                          'h-full rounded-full',
                                          tech.efficiencyScore >= 90
                                            ? 'bg-emerald-500'
                                            : tech.efficiencyScore >= 75
                                              ? 'bg-yellow-500'
                                              : 'bg-rose-500',
                                        )}
                                        style={{ width: `${tech.efficiencyScore}%` }}
                                      />
                                    </div>
                                    <span className="text-xs font-bold">
                                      {tech.efficiencyScore}%
                                    </span>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={8}
                              className="h-24 text-center text-xs text-muted-foreground"
                            >
                              {t('analytics.personnelPerformance.messages.noDataAvailable')}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
